/**
 * Real-time Event Store
 *
 * Scalable, adaptable event system that bridges backend SSE to React components.
 *
 * Architecture:
 * - Single SSE connection managed centrally (prevents connection explosion)
 * - Event dispatcher pattern (easy to add new event types)
 * - Type-safe subscriptions (TypeScript inference for handlers)
 * - Resilient reconnection (exponential backoff)
 *
 * Usage:
 *   // In a component
 *   const { subscribe } = useRealtimeStore()
 *
 *   useEffect(() => {
 *     return subscribe('db_session_changed', (event) => {
 *       console.log('Session updated:', event.data)
 *     })
 *   }, [])
 */

import { create } from 'zustand'

// ============================================================================
// Event Types - Add new backend events here
// ============================================================================

export type RealtimeEventType =
  // Database change events (from PostgreSQL LISTEN/NOTIFY)
  | 'db_session_changed'
  | 'db_task_changed'
  | 'db_feedback_changed'
  // Multi-client sync events
  | 'message_added'
  // Chat/streaming events
  | 'status'
  | 'thought'
  | 'tool_call_start'
  | 'tool_call_progress'
  | 'tool_call_result'
  | 'response_chunk'
  | 'response_complete'
  | 'error'
  // Claude events
  | 'claude_start'
  | 'claude_output'
  | 'claude_complete'
  // Live audio events
  | 'live_session_start'
  | 'live_session_end'
  | 'live_audio'
  | 'live_transcript'
  | 'live_activity'
  // Catch-all for unknown events
  | string

// ============================================================================
// Event Payload Types
// ============================================================================

export interface BaseEvent {
  type: RealtimeEventType
  timestamp: string
  event_id: string
  session_id?: string
}

export interface DbChangeEvent extends BaseEvent {
  type: 'db_session_changed' | 'db_task_changed' | 'db_feedback_changed'
  data: {
    channel: string
    operation: 'INSERT' | 'UPDATE' | 'DELETE'
    table: string
    id: string | number
    data: Record<string, unknown>
  }
}

export interface StatusEvent extends BaseEvent {
  type: 'status'
  data: { message: string; [key: string]: unknown }
}

export interface ToolCallStartEvent extends BaseEvent {
  type: 'tool_call_start'
  data: { tool: string; args: Record<string, unknown> }
}

export interface ToolCallResultEvent extends BaseEvent {
  type: 'tool_call_result'
  data: { tool: string; result: unknown; success: boolean; duration_ms?: number }
}

export interface ThoughtEvent extends BaseEvent {
  type: 'thought'
  data: { text: string }
}

export interface ErrorEvent extends BaseEvent {
  type: 'error'
  data: { error: string; message?: string }
}

export interface MessageAddedEvent extends BaseEvent {
  type: 'message_added'
  data: {
    message_id: string
    role: 'user' | 'assistant'
    content: string
    tool_calls?: Array<{
      name: string
      arguments: Record<string, unknown>
      result?: unknown
    }>
    suggested_actions?: Array<{
      action_type: string
      label: string
      description: string
      prompt: string
    }>
    timestamp: string
  }
}

// Union of all specific event types
export type RealtimeEvent =
  | DbChangeEvent
  | StatusEvent
  | ToolCallStartEvent
  | ToolCallResultEvent
  | ThoughtEvent
  | ErrorEvent
  | MessageAddedEvent
  | (BaseEvent & { data: Record<string, unknown> })

// ============================================================================
// Event Handler Types
// ============================================================================

export type EventHandler<T extends RealtimeEvent = RealtimeEvent> = (event: T) => void

type Unsubscribe = () => void

// ============================================================================
// Connection State
// ============================================================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

// ============================================================================
// Store State
// ============================================================================

interface RealtimeState {
  // Connection state
  connectionState: ConnectionState
  lastEventTime: number | null
  reconnectAttempts: number

  // Stats
  eventsReceived: number

  // Internal state (not for external use)
  _eventSource: EventSource | null
  _handlers: Map<RealtimeEventType, Set<EventHandler>>
  _globalHandlers: Set<EventHandler>

  // Actions
  connect: () => void
  disconnect: () => void
  subscribe: <T extends RealtimeEvent>(eventType: RealtimeEventType, handler: EventHandler<T>) => Unsubscribe
  subscribeAll: (handler: EventHandler) => Unsubscribe

  // Internal actions
  _emit: (event: RealtimeEvent) => void
  _setConnectionState: (state: ConnectionState) => void
}

// ============================================================================
// Constants
// ============================================================================

const MAX_RECONNECT_ATTEMPTS = 10
const BASE_RECONNECT_DELAY = 1000 // 1 second
const MAX_RECONNECT_DELAY = 30000 // 30 seconds

// ============================================================================
// Store Implementation
// ============================================================================

export const useRealtimeStore = create<RealtimeState>((set, get) => ({
  connectionState: 'disconnected',
  lastEventTime: null,
  reconnectAttempts: 0,
  eventsReceived: 0,
  _eventSource: null,
  _handlers: new Map(),
  _globalHandlers: new Set(),

  connect: () => {
    const state = get()

    // Already connected or connecting
    if (state._eventSource && state.connectionState !== 'disconnected') {
      return
    }

    set({ connectionState: 'connecting' })

    // Get auth token
    const token = (() => {
      try {
        const stored = localStorage.getItem('neuro-ha-auth')
        if (stored) {
          return JSON.parse(stored).state?.token
        }
      } catch {
        return null
      }
      return null
    })()

    const sseUrl = `/api/v1/chat/events${token ? `?token=${encodeURIComponent(token)}` : ''}`

    const eventSource = new EventSource(sseUrl)

    eventSource.onopen = () => {
      console.log('[Realtime] SSE connection established')
      set({
        connectionState: 'connected',
        reconnectAttempts: 0,
      })
    }

    eventSource.onerror = (error) => {
      console.warn('[Realtime] SSE connection error:', error)

      const currentState = get()
      eventSource.close()

      // Attempt reconnection with exponential backoff
      const attempts = currentState.reconnectAttempts + 1

      if (attempts <= MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(
          BASE_RECONNECT_DELAY * Math.pow(2, attempts - 1),
          MAX_RECONNECT_DELAY
        )

        console.log(`[Realtime] Reconnecting in ${delay}ms (attempt ${attempts}/${MAX_RECONNECT_ATTEMPTS})`)

        set({
          connectionState: 'reconnecting',
          reconnectAttempts: attempts,
          _eventSource: null,
        })

        setTimeout(() => {
          get().connect()
        }, delay)
      } else {
        console.error('[Realtime] Max reconnection attempts reached')
        set({
          connectionState: 'disconnected',
          _eventSource: null,
        })
      }
    }

    // Handle all events with a single listener
    eventSource.onmessage = (messageEvent) => {
      try {
        const event = JSON.parse(messageEvent.data) as RealtimeEvent
        get()._emit(event)
      } catch (e) {
        console.warn('[Realtime] Failed to parse event:', e)
      }
    }

    // Also listen for named event types (backend sends both formats)
    // Note: 'error' is NOT included here - EventSource 'error' events are connection errors
    // with no .data property and should be handled by onerror, not as named events
    const eventTypes: RealtimeEventType[] = [
      'db_session_changed', 'db_task_changed', 'db_feedback_changed',
      'status', 'thought', 'tool_call_start', 'tool_call_progress',
      'tool_call_result', 'response_chunk', 'response_complete',
      'claude_start', 'claude_output', 'claude_complete',
    ]

    eventTypes.forEach(eventType => {
      eventSource.addEventListener(eventType, (messageEvent: MessageEvent) => {
        try {
          const event = JSON.parse(messageEvent.data) as RealtimeEvent
          // Ensure type is set correctly
          get()._emit({ ...event, type: eventType })
        } catch (e) {
          console.warn(`[Realtime] Failed to parse ${eventType} event:`, e)
        }
      })
    })

    set({ _eventSource: eventSource })
  },

  disconnect: () => {
    const { _eventSource } = get()
    if (_eventSource) {
      _eventSource.close()
    }
    set({
      connectionState: 'disconnected',
      _eventSource: null,
      reconnectAttempts: 0,
    })
  },

  subscribe: <T extends RealtimeEvent>(eventType: RealtimeEventType, handler: EventHandler<T>): Unsubscribe => {
    const { _handlers } = get()

    if (!_handlers.has(eventType)) {
      _handlers.set(eventType, new Set())
    }

    _handlers.get(eventType)!.add(handler as EventHandler)

    // Return unsubscribe function
    return () => {
      const handlers = _handlers.get(eventType)
      if (handlers) {
        handlers.delete(handler as EventHandler)
      }
    }
  },

  subscribeAll: (handler: EventHandler): Unsubscribe => {
    const { _globalHandlers } = get()
    _globalHandlers.add(handler)

    return () => {
      _globalHandlers.delete(handler)
    }
  },

  _emit: (event: RealtimeEvent) => {
    const { _handlers, _globalHandlers } = get()

    set(state => ({
      eventsReceived: state.eventsReceived + 1,
      lastEventTime: Date.now(),
    }))

    // Notify type-specific handlers
    const handlers = _handlers.get(event.type)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(event)
        } catch (e) {
          console.error(`[Realtime] Handler error for ${event.type}:`, e)
        }
      })
    }

    // Notify global handlers
    _globalHandlers.forEach(handler => {
      try {
        handler(event)
      } catch (e) {
        console.error('[Realtime] Global handler error:', e)
      }
    })
  },

  _setConnectionState: (connectionState: ConnectionState) => {
    set({ connectionState })
  },
}))

// ============================================================================
// Selector Hooks (for React components)
// ============================================================================

export const useConnectionState = () => useRealtimeStore(state => state.connectionState)
export const useEventsReceived = () => useRealtimeStore(state => state.eventsReceived)
export const useLastEventTime = () => useRealtimeStore(state => state.lastEventTime)
